<template>

    <SeoHead :seo="seo" />

    <MainLayout>
        <SeoBreadcrumbs :seo="seo" />
        <div class="player-container">
            <!-- Header -->
            <header class="pp-header">
                <div class="pp-header-left">
                    <PhotoCarousel
                        :photos="carouselPhotos"
                        :fallback-url="displayPhotoUrl"
                        :initials="getInitials()"
                        @open-lightbox="onCarouselOpenLightbox"
                    />
                    <PhotoLightbox
                        :open="lightboxOpen"
                        :items="lightboxItems"
                        :index="lightboxIndex"
                        @close="lightboxOpen = false"
                        @update:index="lightboxIndex = $event"
                    />
                    <!-- Contacts under photo -->
                    <div v-if="hasAnyContact" class="pp-contacts">
                        <span v-if="user.instagram" class="pp-contact-wrap">
                            <a :href="instagramLink" target="_blank" rel="noopener" class="pp-contact-link"
                                :title="user.instagram">
                                <img src="/images/instagram.png" alt="Instagram" class="pp-contact-icon" width="24" height="24" loading="lazy" />
                            </a>
                        </span>
                        <span v-if="user.telegram" class="pp-contact-wrap">
                            <a :href="telegramLink" target="_blank" rel="noopener" class="pp-contact-link"
                                :title="user.telegram">
                                <img src="/images/telegram.png" alt="Telegram" class="pp-contact-icon" width="24" height="24" loading="lazy" />
                            </a>
                        </span>
                        <span v-if="user.phone" class="pp-contact-wrap">
                            <a :href="'tel:' + user.phone" class="pp-contact-link" :title="user.phone">
                                <img src="/images/phone.png" alt="Телефон" class="pp-contact-icon" width="24" height="24" loading="lazy" />
                            </a>
                        </span>
                        <span v-if="user.email" class="pp-contact-wrap">
                            <a :href="'mailto:' + user.email" class="pp-contact-link" :title="user.email">
                                <img src="/images/email.png" alt="Email" class="pp-contact-icon" width="24" height="24" loading="lazy" />
                            </a>
                        </span>
                    </div>
                </div>
                <div class="pp-header-info">
                    <div class="pp-name-row">
                        <h1 class="pp-fullname">
                            <RegisteredBadge :status="user.status" />
                            {{ user.full_name }}
                        </h1>
                        <NationalityFlag :code="user.nationality" />
                    </div>
                    <div v-if="user.surname_en || user.first_name_en" class="pp-name-en">
                        {{ [user.surname_en, user.first_name_en].filter(Boolean).join(' ') }}
                    </div>
                    <!-- Info grid (moved here) -->
                    <div class="pp-info-grid" style="margin-top:0.75rem;">
                        <div v-if="user.birth_date" class="pp-info-item">
                            <span class="pp-info-label">Дата рождения</span>
                            <span class="pp-info-value">{{ formatDateShort(user.birth_date) }}<template
                                    v-if="userAge != null"> ({{ userAge }} лет)</template></span>
                        </div>
                        <div v-if="user.height" class="pp-info-item">
                            <span class="pp-info-label">Рост</span>
                            <span class="pp-info-value">{{ user.height }} см</span>
                        </div>
                        <div v-if="user.weight != null" class="pp-info-item">
                            <span class="pp-info-label">Вес</span>
                            <span class="pp-info-value">{{ user.weight }} кг</span>
                        </div>
                        <div v-if="user.jump_height != null" class="pp-info-item">
                            <span class="pp-info-label">Высота прыжка</span>
                            <span class="pp-info-value">{{ user.jump_height }} см</span>
                        </div>
                        <div v-if="user.rank" class="pp-info-item">
                            <span class="pp-info-label">Разряд</span>
                            <span class="pp-info-value">{{ user.rank }}</span>
                        </div>
                        <div v-if="user.handedness" class="pp-info-item">
                            <span class="pp-info-label">Рабочая рука</span>
                            <span class="pp-info-value">{{ handednessLabel(user.handedness) }}</span>
                        </div>
                    </div>
                </div>
            </header>

            <!-- Info cards moved into header-info for compact layout -->
            <!-- (pp-info-section removed; info grid is rendered inside pp-header-info) -->

            <!-- Career history as rows -->
            <section v-if="filteredSeasons.length > 0" class="pp-career-section">
                <h2 class="pp-section-title sh-section-title">Карьера</h2>
                <div class="table-scroll sh-table-scroll">
                <div class="pp-career-table">
                    <div class="pp-career-header-row sh-table-header">
                        <span class="pp-career-col pp-career-col--season">Сезон</span>
                        <span class="pp-career-col pp-career-col--team">Команда</span>
                        <span class="pp-career-col pp-career-col--places places-track">
                            <img src="/images/supercup.png" alt="Суперкубок" class="places-icon" />
                            <img src="/images/cup.png" alt="Кубок" class="places-icon" />
                            <img src="/images/championship.png" alt="Чемпионат" class="places-icon" />
                        </span>
                        <span class="pp-career-col pp-career-col--position">Амплуа</span>
                        <span class="pp-career-col pp-career-col--number">Номер</span>
                        <span class="pp-career-col pp-career-col--photo">Фото</span>
                    </div>
                    <!-- Группировка по сезонам: единый столбец сезона слева, строки записей справа -->
                    <div
                        v-for="(season, seasonIndex) in filteredSeasons"
                        :key="season.id"
                        class="season-group"
                        :class="{ 'sh-row-zebra': seasonIndex % 2 === 1 }"
                    >
                        <div class="season-group-inner">
                            <div class="season-cell pp-career-col pp-career-col--season" data-label="Сезон">
                                <span class="season-label">
                                    <span class="season-label__full">{{ season.name }}</span>
                                    <span class="season-label__short">{{ formatSeasonShort(season.name) }}</span>
                                </span>
                            </div>

                            <div class="season-rows">
                                <template
                                    v-if="season.played && Array.isArray(season.team_stints) && season.team_stints.length">
                                    <div v-for="(stint, sIndex) in season.team_stints"
                                        :key="`${season.id}_stint_${sIndex}`" class="team-stint-group"
                                        :style="{ '--role-rows': stint.roles.length }">
                                        <span
                                            class="pp-career-col pp-career-col--team team-stint-cell--span"
                                            data-label="Команда"
                                        >
                                            <component
                                                :is="teamPublicPath(stint) ? 'a' : 'span'"
                                                :href="teamPublicPath(stint) || undefined"
                                                class="pp-career-team"
                                                :class="{ 'pp-career-team--link': !!teamPublicPath(stint) }"
                                            >
                                                <span v-if="stint.team_logo" class="sh-logo-slot">
                                                    <img
                                                        :src="stint.team_logo"
                                                        :alt="stint.team"
                                                        class="sh-logo-slot__img"
                                                    />
                                                </span>
                                                <span
                                                    v-else
                                                    class="sh-logo-slot"
                                                    aria-hidden="true"
                                                />
                                                <span class="pp-career-team-name">
                                                    <span>{{ stint.team }}</span>
                                                </span>
                                            </component>
                                        </span>

                                        <span
                                            class="pp-career-col pp-career-col--places places-track team-stint-cell--span"
                                            data-label="Места"
                                        >
                                            <div class="places-item"><PlaceBadge :place="stint.places?.supercup" /></div>
                                            <div class="places-item"><PlaceBadge :place="stint.places?.cup" /></div>
                                            <div class="places-item"><PlaceBadge :place="stint.places?.championship" /></div>
                                        </span>

                                        <template v-for="(role, rIndex) in stint.roles"
                                            :key="`${season.id}_${sIndex}_${rIndex}`">
                                            <span
                                                class="pp-career-col pp-career-col--position"
                                                data-label="Амплуа"
                                                :style="{ gridRow: rIndex + 1 }"
                                            >{{ role.position || '—' }}</span>
                                            <span
                                                class="pp-career-col pp-career-col--number"
                                                data-label="Номер"
                                                :style="{ gridRow: rIndex + 1 }"
                                            >
                                                <JerseyNumber
                                                    v-if="role.number != null"
                                                    :number="role.number"
                                                    :primary-color="stint.primary_color"
                                                    :secondary-color="stint.secondary_color"
                                                    size="compact"
                                                />
                                                <template v-else>—</template>
                                            </span>
                                            <span
                                                class="pp-career-col pp-career-col--photo"
                                                data-label="Фото"
                                                :style="{ gridRow: rIndex + 1 }"
                                            >
                                                <button
                                                    v-if="role.photo_url"
                                                    type="button"
                                                    class="sh-photo-thumb"
                                                    :title="role.position || 'Фото'"
                                                    @click="onRolePhotoClick(role.photo_url)"
                                                >
                                                    <img
                                                        :src="role.photo_url"
                                                        :alt="role.position || 'Фото'"
                                                        class="sh-photo-thumb__img"
                                                    />
                                                </button>
                                            </span>
                                        </template>
                                    </div>
                                </template>
                                <template v-else>
                                    <div class="pp-career-row pp-career-row--inactive">
                                        <span
                                            class="pp-career-col pp-career-col--team pp-career-not-played"
                                            data-label="Команда"
                                        >не участвовал</span>
                                        <span class="pp-career-col pp-career-col--places" data-label="Места"></span>
                                        <span class="pp-career-col pp-career-col--position" data-label="Амплуа"></span>
                                        <span class="pp-career-col pp-career-col--number" data-label="Номер"></span>
                                        <span class="pp-career-col pp-career-col--photo" data-label="Фото"></span>
                                    </div>
                                </template>
                            </div>
                        </div>
                    </div>
                </div>
                </div>
            </section>

            <!-- Owner actions -->
            <div v-if="isOwner" class="pp-actions">
                <Link v-if="hasPlayerCareer" href="/player/stats" class="pp-action-btn pp-action-btn--primary">Моя статистика</Link>
                <Link href="/profile" class="pp-action-btn">Редактировать профиль</Link>
            </div>
        </div>
    </MainLayout>
</template>

<script setup>
import { computed, ref } from 'vue';
import { Link } from '@inertiajs/vue3';
import SeoHead from '@/Components/SeoHead.vue';
import SeoBreadcrumbs from '@/Components/SeoBreadcrumbs.vue';
import MainLayout from '@/components/layout/MainLayout.vue';
import PhotoCarousel from '@/components/player/PhotoCarousel.vue';
import PlaceBadge from '@/components/player/PlaceBadge.vue';
import JerseyNumber from '@/Components/shared/JerseyNumber.vue';
import NationalityFlag from '@/Components/shared/NationalityFlag.vue';
import RegisteredBadge from '@/Components/shared/RegisteredBadge.vue';
import PhotoLightbox from '@/Components/ui/PhotoLightbox.vue';
import { teamPublicPath } from '@/utils/teamUrl';
import { formatSeasonShort } from '@/utils/formatSeason';

const props = defineProps({
    seo: { type: Object, default: null },
    user: { type: Object, required: true },
    seasons: { type: Array, default: () => [] },
    carouselPhotos: { type: Array, default: () => [] },
    positions: { type: Array, default: () => [] },
    numbers: { type: Array, default: () => [] },
    hasPlayerCareer: { type: Boolean, default: false },
    isOwner: { type: Boolean, default: false },
    latestSeasonPhotoUrl: { type: String, default: null },
});

const lightboxOpen = ref(false);
const lightboxIndex = ref(0);

const lightboxItems = computed(() =>
    (props.carouselPhotos || [])
        .filter((photo) => photo?.url)
        .map((photo) => ({
            url: photo.url,
            caption: photo.caption || photo.seasonName || photo.season_name || '',
        }))
);

function openLightbox(index = 0) {
    if (!lightboxItems.value.length) return;
    lightboxIndex.value = Math.max(0, Math.min(index, lightboxItems.value.length - 1));
    lightboxOpen.value = true;
}

function onCarouselOpenLightbox(payload) {
    const url = payload?.url;
    const idx = url
        ? lightboxItems.value.findIndex((item) => item.url === url)
        : (payload?.index ?? 0);
    openLightbox(idx >= 0 ? idx : 0);
}

function onRolePhotoClick(url) {
    if (!url) return;
    const idx = lightboxItems.value.findIndex((item) => item.url === url);
    if (idx < 0) return;
    openLightbox(idx);
}
const displayPhotoUrl = computed(() => {
    const firstWithUrl = (props.carouselPhotos || []).find(photo => photo.url);
    return firstWithUrl?.url ?? props.latestSeasonPhotoUrl ?? props.user?.photo_url ?? null;
});

const filteredSeasons = computed(() => {
    if (!Array.isArray(props.seasons) || props.seasons.length === 0) return [];
    return props.seasons
        .filter(s => {
            const hasStints = Array.isArray(s.team_stints) && s.team_stints.length > 0;
            return Boolean(s.played) || hasStints;
        })
        .sort((a, b) => Number(b.id) - Number(a.id));
});

const userAge = computed(() => {
    const d = props.user?.birth_date;
    if (!d) return null;
    const birth = new Date(d);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age;
});

const hasAnyContact = computed(() =>
    !!(props.user?.instagram || props.user?.telegram || props.user?.phone || props.user?.email)
);

const instagramLink = computed(() => {
    const v = props.user?.instagram;
    if (!v) return '#';
    const clean = v.replace(/^@/, '');
    return 'https://instagram.com/' + clean;
});

const telegramLink = computed(() => {
    const v = props.user?.telegram;
    if (!v) return '#';
    const clean = v.replace(/^@/, '');
    return 'https://t.me/' + clean;
});

const handednessOptions = [
    { value: 'right', label: 'Правая' },
    { value: 'left', label: 'Левая' },
    { value: 'ambidextrous', label: 'Обе' },
];

function handednessLabel(value) {
    return handednessOptions.find(o => o.value === value)?.label ?? value;
}

function getInitials() {
    const parts = [props.user.surname, props.user.first_name]
        .filter(Boolean)
        .map(part => part.charAt(0).toUpperCase());
    return parts.join('');
}

function formatDateShort(dateString) {
    if (!dateString) return '';
    const date = new Date(dateString);
    const d = date.getDate().toString().padStart(2, '0');
    const m = (date.getMonth() + 1).toString().padStart(2, '0');
    const y = date.getFullYear();
    return `${d}.${m}.${y}`;
}
</script>

<style lang="scss" scoped>
@import '../../../scss/tokens';

.player-container {
    width: 100%;
    background: $color-bg-primary;
    border-radius: 16px;
    box-shadow: $shadow-sm;
    overflow: hidden;
    border: 1px solid $color-border;
    padding: 1.25rem 1.5rem;
}

/* ============ HEADER ============ */
.pp-header {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1.25rem;
    padding: 1rem 0 1.5rem;
    border-bottom: 1px solid $color-border;
    margin-bottom: 1.5rem;
    background: transparent;
}

.pp-header-left {
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.6rem;
    width: 100%;
}

.pp-header-left :deep(.photo-carousel) {
    width: auto;
    max-width: 100%;
}

.pp-header-photo {
    width: 90px;
    height: 120px;
    border-radius: 10px;
    overflow: hidden;
    background: $color-bg-light;
}

.pp-photo-img {
    width: 100%;
    height: 100%;
    @include person-photo-cover;
}

.pp-photo-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2rem;
    font-weight: 700;
    color: $color-text-light;
    background: $color-bg-lighter;
}

.pp-header-info {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    padding-top: 0.25rem;
}

.pp-name-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
}

.pp-fullname {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 1.25rem;
    font-weight: 700;
    color: $color-text-result-primary;
    margin: 0;
    line-height: 1.3;
}

.pp-name-en {
    font-size: 0.95rem;
    color: #64748b;
    margin-top: 0.25rem;
}

/* ============ INFO SECTION ============ */
.pp-info-section {
    margin-bottom: 2rem;
    display: none;
    /* removed: info grid moved into header */
}

.pp-info-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem 1rem;
}

.pp-info-item {
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
    min-width: 120px;
    padding: 0.5rem 0;
}

.pp-info-label {
    font-size: 0.75rem;
    color: $color-text-result-secondary;
    text-transform: uppercase;
    letter-spacing: 0.03em;
}

.pp-info-value {
    font-size: 0.9375rem;
    font-weight: 600;
    color: $color-text-result-primary;
}

/* ============ CONTACTS ============ */
.pp-contacts {
    display: flex;
    gap: 0.4rem;
    justify-content: center;
}

.pp-contact-link {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 8px;
    background: $color-bg-lighter;
    transition: background 0.2s;
}

.pp-contact-link:hover {
    background: $color-primary-alpha-2;
}

.pp-contact-icon {
    width: 16px;
    height: 16px;
    object-fit: contain;
}

/* ============ CAREER TABLE ============ */
.pp-career-section {
    margin-bottom: var(--sh-section-mb);
}

.pp-career-table {
    --career-cols: var(--sh-season-col) minmax(9rem, 1fr) 14rem 7.5rem 3.75rem var(--sh-photo-col);
    --career-gap: var(--sh-gap);
    --career-pad-x: var(--sh-pad-x);
    min-width: 48.5rem;
    border: 1px solid $color-border;
    border-radius: 10px;
}

/* Shared column contract: header and body use the same tracks */
.pp-career-header-row,
.season-group-inner {
    display: grid;
    grid-template-columns: var(--career-cols);
    column-gap: var(--career-gap);
    padding-inline: var(--career-pad-x);
    align-items: center;
}

.pp-career-header-row {
    padding-block: 0.5rem;
    border-bottom: 1px solid $color-border;
    font-size: 0.75rem;
    font-weight: 600;
    color: $color-text-result-secondary;
    text-transform: uppercase;
    letter-spacing: 0.03em;
}

.season-group {
    border-top: 1px solid $color-border-lighter;
    padding: 0;
}

.season-group-inner {
    grid-auto-rows: auto;
    align-items: stretch;
}

.season-cell {
    grid-column: 1;
    padding-block: 0.6rem;
    font-weight: 600;
    font-size: 0.8125rem;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    min-width: 0;
    white-space: nowrap;
}

/* Inherit team|places|position|number tracks from season-group-inner */
.season-rows {
    grid-column: 2 / -1;
    display: grid;
    grid-template-columns: subgrid;
    align-content: start;
}

.team-stint-group,
.pp-career-row {
    grid-column: 1 / -1;
    display: grid;
    grid-template-columns: subgrid;
    grid-template-rows: repeat(var(--role-rows, 1), auto);
    align-items: center;
    padding-block: 0.6rem;
    border-bottom: 1px solid $color-border-lighter;
    font-size: 0.875rem;
    transition: background 0.15s;

    &:last-child {
        border-bottom: none;
    }

    &:hover {
        background: $color-bg-light;
    }
}

.team-stint-cell--span {
    grid-row: 1 / -1;
    align-self: center;
}

.team-stint-group .pp-career-col--team {
    grid-column: 1;
}

.team-stint-group .pp-career-col--places {
    grid-column: 2;
}

.team-stint-group .pp-career-col--position {
    grid-column: 3;
}

.team-stint-group .pp-career-col--number {
    grid-column: 4;
}

.team-stint-group .pp-career-col--photo {
    grid-column: 5;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.pp-career-row--inactive {
    .pp-career-col {
        color: $color-text-light;
    }
}

.pp-career-col {
    color: $color-text-result-primary;
    min-width: 0;
}

.pp-career-col--season {
    font-weight: 600;
    font-size: 0.8125rem;
}

.pp-career-col--team {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 500;
}

.pp-career-team {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 500;
    min-width: 0;

    &--link {
        color: inherit;
        text-decoration: none;

        &:hover .pp-career-team-name {
            text-decoration: underline;
        }
    }
}

.pp-career-team-name {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
    min-width: 0;

    > span {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
}

.pp-career-col--position {
    text-align: center;
}

.pp-career-col--number {
    text-align: center;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.pp-career-col--photo {
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.pp-career-row--inactive .pp-career-col--number {
    color: $color-text-light;
}

.pp-career-not-played {
    font-style: italic;
    color: $color-text-light;
}

.pp-career-col--places {
    color: $color-text-result-primary;
    font-size: 0.875rem;
    padding-block: 0.25rem;
    justify-self: center;
}

/* ============ OWNER ACTIONS ============ */
.pp-actions {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;

    @include up($bp-sm) {
        flex-direction: row;
    }
}

.pp-action-btn {
    padding: 0.6rem 1.25rem;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 600;
    text-decoration: none;
    color: $color-text-result-primary;
    background: $color-bg-lighter;
    border: 1px solid $color-border;
    transition: all 0.2s;

    &:hover {
        background: $color-bg-light;
        border-color: $color-border-dark;
    }
}

.pp-action-btn--primary {
    background: $color-primary;
    color: #fff;
    border-color: $color-primary;

    &:hover {
        background: $color-primary-hover;
        border-color: $color-primary-hover;
    }
}

/* ============ RESPONSIVE ============ */
@include up($bp-sm) {
    .pp-header {
        flex-direction: row;
        align-items: flex-start;
    }

    .pp-header-left {
        width: auto;
    }

    .pp-header-photo {
        width: 120px;
        height: 160px;
    }

    .pp-fullname {
        font-size: 1.5rem;
    }

    .pp-info-grid {
        gap: 0.5rem 1.5rem;
    }

    .pp-info-item {
        min-width: 140px;
    }

    .pp-contact-link {
        width: 36px;
        height: 36px;
    }

    .pp-contact-icon {
        width: 20px;
        height: 20px;
    }
}
</style>
